import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
    query,
    where,
    limit,
    orderBy,
    startAfter,
    onSnapshot,
    Unsubscribe,
    FirestoreDataConverter,
    Timestamp,
    serverTimestamp,
    deleteField,
    updateDoc
} from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';
import { StepNodeScreenService } from './StepNodeScreenService';
import {
    Onboarding,
    OnboardingVersion,
    OnboardingVersionMetadata,
    OnboardingThemeSettings,
    OnboardingThemeSettingsV2,
    onboardingConverter,
    onboardingVersionConverter
} from '../converters/onboardingConverter';

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

function stripUndefinedDeep<T>(value: T): T {
    if (value === undefined) {
        return null as T;
    }

    if (Array.isArray(value)) {
        return value.map((item) => stripUndefinedDeep(item)) as T;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    const result: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
        if (child !== undefined) {
            result[key] = stripUndefinedDeep(child);
        }
    }

    return result as T;
}

/**
 * OnboardingService
 * 
 * Manages Onboarding entities and their versioned V2 metadata.
 * 
 * Structure:
 * - projects/{projectId}/onboardings/{onboardingId} (Parent)
 * - projects/{projectId}/onboardings/{onboardingId}/versions/{versionId} (Child)
 */
export class OnboardingService {
    private readonly collectionName = 'onboardings';
    private readonly versionsCollectionName = 'versions';
    private readonly themeSettingsCollectionName = 'themeSettings';
    private readonly themeSettingsV2CollectionName = 'themeSettingsV2';
    private readonly themeSettingsDocId = 'settings'; // Single doc per onboarding
    private readonly firestore = CoreFirebaseService.getInstance().firestore;

    private readonly converter: FirestoreDataConverter<Onboarding>;
    private readonly versionConverter: FirestoreDataConverter<OnboardingVersion>;

    constructor(
        converter: FirestoreDataConverter<Onboarding> = onboardingConverter,
        versionConverter: FirestoreDataConverter<OnboardingVersion> = onboardingVersionConverter
    ) {
        this.converter = converter;
        this.versionConverter = versionConverter;
    }

    // ============================================================================
    // PARENT ONBOARDING OPERATIONS
    // ============================================================================

    /**
     * Create a new Onboarding container
     */
    async createOnboarding(projectId: string, data: Partial<Onboarding>): Promise<Onboarding> {
        try {
            const collectionRef = collection(this.firestore, `projects/${projectId}/${this.collectionName}`);
            const docRef = doc(collectionRef).withConverter(this.converter);

            const onboardingData: Onboarding = {
                ...data,
                id: docRef.id,
                projectId,
                folderId: data.folderId ?? null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            } as Onboarding;

            await setDoc(docRef, onboardingData);
            return onboardingData;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.createOnboarding');
            throw error;
        }
    }

    /**
     * Get Onboarding by ID
     */
    async getOnboarding(projectId: string, onboardingId: string): Promise<Onboarding | null> {
        try {
            const docRef = doc(this.firestore, `projects/${projectId}/${this.collectionName}`, onboardingId).withConverter(this.converter);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return null;
            }

            return { ...docSnap.data(), id: docSnap.id } as Onboarding;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.getOnboarding');
            throw error;
        }
    }

    /**
     * Article-kind exclusion (v2.19.0).
     *
     * Article root documents live in the SAME onboardings collection with a
     * `kind: 'article'` discriminator. Every funnel-facing list/picker must
     * exclude them; filtering is client-side because a Firestore
     * `where('kind', '!=', ...)` clause would also drop the legacy docs that
     * have no `kind` field at all.
     */
    private static excludeArticleKind(items: Onboarding[]): Onboarding[] {
        return items.filter(item => item.kind !== 'article');
    }

    /**
     * List Onboardings for a project (article-kind docs excluded)
     */
    async listOnboardings(projectId: string): Promise<Onboarding[]> {
        try {
            const collectionRef = collection(this.firestore, `projects/${projectId}/${this.collectionName}`).withConverter(this.converter);
            const q = query(collectionRef, orderBy('updatedAt', 'desc'));
            const querySnapshot = await getDocs(q);

            return OnboardingService.excludeArticleKind(
                querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
            );
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.listOnboardings');
            throw error;
        }
    }

    /**
     * List article root documents (kind === 'article') for the Articles UI.
     * Ordered by updatedAt desc; same collection as funnels, discriminated by kind.
     */
    async listArticleRoots(projectId: string): Promise<Onboarding[]> {
        try {
            const collectionRef = collection(this.firestore, `projects/${projectId}/${this.collectionName}`).withConverter(this.converter);
            const q = query(collectionRef, orderBy('updatedAt', 'desc'));
            const querySnapshot = await getDocs(q);

            return querySnapshot.docs
                .map(doc => ({ ...doc.data(), id: doc.id }))
                .filter(item => item.kind === 'article');
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.listArticleRoots');
            throw error;
        }
    }

    /**
     * Update an existing Onboarding
     *
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID to update
     * @param updates - Partial updates (name, defaultLanguage, languages, etc.)
     */
    async updateOnboarding(
        projectId: string,
        onboardingId: string,
        updates: Partial<Omit<Onboarding, 'id' | 'projectId' | 'createdAt'>>
    ): Promise<void> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}`,
                onboardingId
            ).withConverter(this.converter);

            await setDoc(docRef, {
                ...updates,
                updatedAt: new Date().toISOString()
            } as Partial<Onboarding>, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.updateOnboarding');
            throw error;
        }
    }

    // ============================================================================
    // FOLDER OPERATIONS
    // ============================================================================

    /**
     * List Onboardings in a specific folder
     *
     * @param projectId - Project ID
     * @param folderId - Folder ID to filter by
     * @returns Onboardings in the folder, ordered by updatedAt desc
     */
    async listByFolder(projectId: string, folderId: string): Promise<Onboarding[]> {
        try {
            const collectionRef = collection(this.firestore, `projects/${projectId}/${this.collectionName}`).withConverter(this.converter);
            const q = query(
                collectionRef,
                where('folderId', '==', folderId),
                orderBy('updatedAt', 'desc')
            );
            const querySnapshot = await getDocs(q);

            return OnboardingService.excludeArticleKind(
                querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
            );
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.listByFolder');
            throw error;
        }
    }

    /**
     * List Onboardings at root level (no folder)
     *
     * @param projectId - Project ID
     * @returns Root-level onboardings (folderId is null), ordered by updatedAt desc
     */
    async listRootOnboardings(projectId: string): Promise<Onboarding[]> {
        try {
            const collectionRef = collection(this.firestore, `projects/${projectId}/${this.collectionName}`).withConverter(this.converter);
            const q = query(
                collectionRef,
                where('folderId', '==', null),
                orderBy('updatedAt', 'desc')
            );
            const querySnapshot = await getDocs(q);

            return OnboardingService.excludeArticleKind(
                querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))
            );
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.listRootOnboardings');
            throw error;
        }
    }

    /**
     * Move an Onboarding to a different folder
     *
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID to move
     * @param folderId - Target folder ID (or null for root)
     */
    async moveToFolder(projectId: string, onboardingId: string, folderId: string | null): Promise<void> {
        try {
            const docRef = doc(this.firestore, `projects/${projectId}/${this.collectionName}`, onboardingId).withConverter(this.converter);

            await setDoc(docRef, {
                folderId,
                updatedAt: new Date().toISOString()
            } as Partial<Onboarding>, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.moveToFolder');
            throw error;
        }
    }

    // ============================================================================
    // THEME SETTINGS OPERATIONS (Subcollection)
    // ============================================================================

    /**
     * Get theme settings from subcollection
     *
     * Theme settings are stored in a subcollection to avoid loading heavy data
     * with every list query. Only loaded when needed (e.g., canvas, editing).
     *
     * Path: projects/{projectId}/onboardings/{onboardingId}/themeSettings/settings
     *
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID
     * @returns Theme settings or null if not set
     */
    async getThemeSettings(
        projectId: string,
        onboardingId: string
    ): Promise<OnboardingThemeSettings | null> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.themeSettingsCollectionName}`,
                this.themeSettingsDocId
            );
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return null;
            }

            return docSnap.data() as OnboardingThemeSettings;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.getThemeSettings');
            throw error;
        }
    }

    /**
     * Save theme settings to subcollection
     *
     * Creates or replaces the theme settings document.
     * Also updates parent's updatedAt timestamp.
     *
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID
     * @param themeSettings - Theme settings to save
     */
    async saveThemeSettings(
        projectId: string,
        onboardingId: string,
        themeSettings: OnboardingThemeSettings
    ): Promise<void> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.themeSettingsCollectionName}`,
                this.themeSettingsDocId
            );

            await setDoc(docRef, stripUndefinedDeep(themeSettings));

            // Update parent's updatedAt
            const parentRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}`,
                onboardingId
            );
            await setDoc(parentRef, {
                updatedAt: new Date().toISOString()
            }, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.saveThemeSettings');
            throw error;
        }
    }

    /**
     * Delete theme settings subcollection
     *
     * Used when deleting an onboarding.
     *
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID
     */
    async deleteThemeSettings(
        projectId: string,
        onboardingId: string
    ): Promise<void> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.themeSettingsCollectionName}`,
                this.themeSettingsDocId
            );
            await deleteDoc(docRef);
        } catch (error) {
            // Ignore if doesn't exist
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.deleteThemeSettings');
        }
    }

    // ============================================================================
    // V2 THEME SETTINGS OPERATIONS (themeSettingsV2 subcollection)
    // ============================================================================

    /**
     * Get V2 theme settings from subcollection
     *
     * Path: projects/{projectId}/onboardings/{onboardingId}/themeSettingsV2/settings
     */
    async getThemeSettingsV2(
        projectId: string,
        onboardingId: string
    ): Promise<OnboardingThemeSettingsV2 | null> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.themeSettingsV2CollectionName}`,
                this.themeSettingsDocId
            );
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return null;
            }

            return docSnap.data() as OnboardingThemeSettingsV2;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.getThemeSettingsV2');
            throw error;
        }
    }

    /**
     * Save V2 theme settings to subcollection
     *
     * Creates or replaces the V2 theme settings document.
     * Also updates parent's updatedAt timestamp.
     */
    async saveThemeSettingsV2(
        projectId: string,
        onboardingId: string,
        themeSettings: OnboardingThemeSettingsV2
    ): Promise<void> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.themeSettingsV2CollectionName}`,
                this.themeSettingsDocId
            );

            await setDoc(docRef, stripUndefinedDeep(themeSettings));

            // Update parent's updatedAt
            const parentRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}`,
                onboardingId
            );
            await setDoc(parentRef, {
                updatedAt: new Date().toISOString()
            }, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.saveThemeSettingsV2');
            throw error;
        }
    }

    /**
     * Forget where the onboarding's theme came from (`sourceThemeId` / `sourceThemeKind`).
     *
     * Called after Import V2 Theme JSON: the imported theme no longer comes from the theme the
     * onboarding was created with or switched to, so the theme picker must not mark that one
     * as "Current" (onboarding theme switch plan, AC-022).
     */
    async clearThemeSource(projectId: string, onboardingId: string): Promise<void> {
        try {
            const parentRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}`,
                onboardingId
            );
            await setDoc(parentRef, {
                sourceThemeId: deleteField(),
                sourceThemeKind: deleteField(),
                updatedAt: new Date().toISOString(),
            }, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.clearThemeSource');
            throw error;
        }
    }

    /**
     * Delete V2 theme settings subcollection
     */
    async deleteThemeSettingsV2(
        projectId: string,
        onboardingId: string
    ): Promise<void> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.themeSettingsV2CollectionName}`,
                this.themeSettingsDocId
            );
            await deleteDoc(docRef);
        } catch (error) {
            // Ignore if doesn't exist
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.deleteThemeSettingsV2');
        }
    }

    // ============================================================================
    // VERSION OPERATIONS
    // ============================================================================

    /**
     * Create a new version for an onboarding
     * 
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID
     * @param versionMetadata - V2 version metadata stored under the historical Firestore field name
     * @param name - Version name
     * @param createUserId - ID of user creating the version
     * @param sourceVersionId - Optional ID of version to clone from, for tracking
     */
    async createVersion(
        projectId: string,
        onboardingId: string,
        versionMetadata: OnboardingVersionMetadata,
        name: string,
        createUserId: string,
        sourceVersionId?: string
    ): Promise<OnboardingVersion> {
        try {
            const versionsRef = collection(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}`
            );
            const docRef = doc(versionsRef).withConverter(this.versionConverter);
            const now = new Date().toISOString();

            // Legacy inline V1 screens are ignored. V2 screen documents live in screensV2.
            const versionMetadataWithoutScreens: OnboardingVersionMetadata = { ...versionMetadata };
            delete versionMetadataWithoutScreens.screens;

            // Create version document with metadata only
            const versionData: OnboardingVersion = {
                id: docRef.id,
                onboardingId,
                name,
                createdAt: now,
                createUserId,
                parentVersionId: sourceVersionId,
                screensGraph: {
                    ...versionMetadataWithoutScreens,
                    screens: {}
                }
            };

            await setDoc(docRef, versionData);

            // Update parent with latestVersionId
            const parentRef = doc(this.firestore, `projects/${projectId}/${this.collectionName}`, onboardingId);
            await setDoc(parentRef, {
                latestVersionId: docRef.id,
                updatedAt: now
            }, { merge: true });

            return versionData;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.createVersion');
            throw error;
        }
    }

    /**
     * Get a specific version
     */
    async getVersion(projectId: string, onboardingId: string, versionId: string): Promise<OnboardingVersion | null> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}`,
                versionId
            ).withConverter(this.versionConverter);

            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                return null;
            }

            return { ...docSnap.data(), id: docSnap.id } as OnboardingVersion;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.getVersion');
            throw error;
        }
    }

    /**
     * Update an existing version
     * 
     * Useful for saving drafts or updating metadata.
     * 
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID
     * @param versionId - Version ID to update
     * @param updates - Partial updates (e.g. screensGraph, name)
     */
    async updateVersion(
        projectId: string,
        onboardingId: string,
        versionId: string,
        updates: Partial<OnboardingVersion>
    ): Promise<void> {
        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}`,
                versionId
            ).withConverter(this.versionConverter);

            // Ensure we don't overwrite ID or immutable fields if passed by mistake, 
            // though Firestore merge would handle most. 
            // Explicitly handling updatedAt if we added it to version (we didn't in interface, but good practice).

            // We use setDoc with merge to ensure converter logic runs (like Date -> Timestamp)
            await setDoc(docRef, updates as OnboardingVersion, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.updateVersion');
            throw error;
        }
    }

    /**
     * Update the version-level animation defaults on `screensGraph`
     * (`defaultTransition` / `defaultScreenAnimation` / `perKindTransitions`)
     * — the fields the publish pipeline (`WebPlacementConfigLoader`) bakes
     * onto the runtime FlowDocument.
     *
     * Semantics per field: object → set; `null` → REMOVE from the version doc
     * (scheme "None" must erase the default, not write null); `undefined` →
     * leave untouched. Uses dot-path `updateDoc` so sibling `screensGraph.*`
     * fields (screens, launchScreenId, …) are never clobbered.
     */
    async updateVersionAnimationDefaults(
        projectId: string,
        onboardingId: string,
        versionId: string,
        defaults: {
            defaultTransition?: Record<string, unknown> | null;
            defaultScreenAnimation?: Record<string, unknown> | null;
            perKindTransitions?: Record<string, unknown> | null;
        }
    ): Promise<void> {
        const updates: Record<string, unknown> = {};
        if (defaults.defaultTransition !== undefined) {
            updates['screensGraph.defaultTransition'] =
                defaults.defaultTransition === null ? deleteField() : defaults.defaultTransition;
        }
        if (defaults.defaultScreenAnimation !== undefined) {
            updates['screensGraph.defaultScreenAnimation'] =
                defaults.defaultScreenAnimation === null ? deleteField() : defaults.defaultScreenAnimation;
        }
        if (defaults.perKindTransitions !== undefined) {
            updates['screensGraph.perKindTransitions'] =
                defaults.perKindTransitions === null ? deleteField() : defaults.perKindTransitions;
        }
        if (Object.keys(updates).length === 0) return;

        try {
            const docRef = doc(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}`,
                versionId
            );
            await updateDoc(docRef, updates);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.updateVersionAnimationDefaults');
            throw error;
        }
    }

    /**
     * Get the latest version for an onboarding
     */
    async getLatestVersion(projectId: string, onboardingId: string): Promise<OnboardingVersion | null> {
        try {
            // First try to get ID from parent
            const parent = await this.getOnboarding(projectId, onboardingId);
            if (parent?.latestVersionId) {
                return this.getVersion(projectId, onboardingId, parent.latestVersionId);
            }

            // Fallback: query by date
            const versionsRef = collection(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}`
            ).withConverter(this.versionConverter);

            const q = query(versionsRef, orderBy('createdAt', 'desc'), limit(1));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                return null;
            }

            const doc = querySnapshot.docs[0];
            return { ...doc.data(), id: doc.id } as OnboardingVersion;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.getLatestVersion');
            throw error;
        }
    }

    /**
     * List versions for an onboarding
     */
    async listVersions(projectId: string, onboardingId: string): Promise<OnboardingVersion[]> {
        try {
            const versionsRef = collection(
                this.firestore,
                `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}`
            ).withConverter(this.versionConverter);

            const q = query(versionsRef, orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);

            return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.listVersions');
            throw error;
        }
    }

    /**
     * Publish a version (mark as published on parent)
     */
    async publishVersion(projectId: string, onboardingId: string, versionId: string): Promise<void> {
        try {
            const parentRef = doc(this.firestore, `projects/${projectId}/${this.collectionName}`, onboardingId);
            await setDoc(parentRef, {
                publishedVersionId: versionId,
                updatedAt: new Date().toISOString()
            }, { merge: true });
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.publishVersion');
            throw error;
        }
    }

    /**
     * Delete an onboarding and all its versions (cascade delete)
     */
    async deleteOnboarding(projectId: string, onboardingId: string): Promise<void> {
        try {
            const onboarding = await this.getOnboarding(projectId, onboardingId);
            if (!onboarding) {
                return;
            }
            if (onboarding.screenGraphType !== 'v2') {
                throw new Error('Legacy V1 onboarding deletion has been retired. Convert or remove V1 data with the dedicated cleanup migration.');
            }

            // 1. Delete all V2 versions and their StepNode screens
            const versions = await this.listVersions(projectId, onboardingId);
            const screenService = new StepNodeScreenService();

            for (const version of versions) {
                // Delete all screens in this version
                const screens = await screenService.listScreens(projectId, onboardingId, version.id);
                for (const screen of screens) {
                    await screenService.deleteScreen(projectId, onboardingId, version.id, screen.id);
                }

                // Delete the version document
                const versionRef = doc(
                    this.firestore,
                    `projects/${projectId}/${this.collectionName}/${onboardingId}/${this.versionsCollectionName}`,
                    version.id
                );
                await deleteDoc(versionRef);
            }

            // 2. Delete V2 theme settings subcollection
            await this.deleteThemeSettingsV2(projectId, onboardingId);

            // 3. Delete the onboarding document
            const onboardingRef = doc(this.firestore, `projects/${projectId}/${this.collectionName}`, onboardingId);
            await deleteDoc(onboardingRef);
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.deleteOnboarding');
            throw error;
        }
    }

    /**
     * Clone a version (create a new version based on an existing one)
     *
     * Automatically applies locale transformation (A+B logic) if the Onboarding
     * has language settings that differ from the source version.
     *
     * @param projectId - Project ID
     * @param onboardingId - Onboarding ID
     * @param sourceVersionId - Version to clone from
     * @param newVersionName - Name for the new version
     * @param createUserId - User creating the new version
     */
    async cloneVersion(
        projectId: string,
        onboardingId: string,
        sourceVersionId: string,
        newVersionName: string,
        createUserId: string
    ): Promise<OnboardingVersion> {
        throw new Error('Legacy V1 version cloning has been retired. V2 version creation copies StepNode screens through the V2 editor actions.');
    }

    /**
     * Clone an entire onboarding with all versions and screens
     * 
     * @param projectId - Project ID
     * @param sourceOnboardingId - Onboarding to clone
     * @param newName - Name for the cloned onboarding
     * @param createUserId - User creating the clone
     */
    async cloneOnboarding(
        projectId: string,
        sourceOnboardingId: string,
        newName: string,
        createUserId: string
    ): Promise<Onboarding> {
        try {
            // 1. Get source onboarding
            const sourceOnboarding = await this.getOnboarding(projectId, sourceOnboardingId);
            if (!sourceOnboarding) {
                throw new Error(`Source onboarding ${sourceOnboardingId} not found`);
            }
            if (sourceOnboarding.screenGraphType !== 'v2') {
                throw new Error('Legacy V1 onboarding cloning has been retired. Convert or remove V1 data with the dedicated cleanup migration.');
            }

            // 2. Create new onboarding (preserve all metadata from source)
            const newOnboarding = await this.createOnboarding(projectId, {
                name: newName,
                folderId: sourceOnboarding.folderId,
                sourceThemeId: sourceOnboarding.sourceThemeId,
                defaultLanguage: sourceOnboarding.defaultLanguage,
                languages: sourceOnboarding.languages,
                platform: sourceOnboarding.platform,
                screenGraphType: sourceOnboarding.screenGraphType,
                kind: sourceOnboarding.kind,
            });

            // 2b. Copy V2 theme subcollection
            const sourceThemeSettingsV2 = await this.getThemeSettingsV2(projectId, sourceOnboardingId);
            if (sourceThemeSettingsV2) {
                await this.saveThemeSettingsV2(projectId, newOnboarding.id, sourceThemeSettingsV2);
            }

            // 3. Get all versions from source
            const sourceVersions = (await this.listVersions(projectId, sourceOnboardingId))
                .slice()
                .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));

            // 4. Clone each version with all its screens
            const screenService = new StepNodeScreenService();

            const versionIdMap: Record<string, string> = {}; // old ID -> new ID

            for (const sourceVersion of sourceVersions) {
                const sourceVersionRef = doc(
                    this.firestore,
                    `projects/${projectId}/${this.collectionName}/${sourceOnboardingId}/${this.versionsCollectionName}`,
                    sourceVersion.id
                );
                const sourceVersionSnap = await getDoc(sourceVersionRef);
                const sourceVersionData = sourceVersionSnap.data() as { variables?: Record<string, unknown> } | undefined;
                const sourceVariables = sourceVersionData?.variables;

                // Get all StepNode screens from this V2 version
                const screens = await screenService.listScreens(projectId, sourceOnboardingId, sourceVersion.id);

                // Create new version
                const newVersion = await this.createVersion(
                    projectId,
                    newOnboarding.id,
                    {
                        ...sourceVersion.screensGraph,
                        screens: {}
                    },
                    sourceVersion.name,
                    createUserId,
                    sourceVersion.parentVersionId ? versionIdMap[sourceVersion.parentVersionId] : undefined
                );

                // Batch write cloned StepNode screens to screensV2 subcollection
                if (screens.length > 0) {
                    const batch = CoreFirebaseService.getInstance().batch();
                    const screensV2Path = `projects/${projectId}/${this.collectionName}/${newOnboarding.id}/${this.versionsCollectionName}/${newVersion.id}/screensV2`;
                    for (const screen of screens) {
                        const screenRef = doc(this.firestore, screensV2Path, (screen as any).id);
                        batch.set(screenRef, screen as any);
                    }
                    await batch.commit();
                }

                // Copy variables (FunnelVariable definitions stored at version level).
                // Read from the raw version document to preserve exact map keys,
                // variable.id values, and enum options[].id values.
                if (sourceVariables) {
                    await this.updateVersion(projectId, newOnboarding.id, newVersion.id, {
                        variables: sourceVariables
                    } as Partial<OnboardingVersion>);
                }

                versionIdMap[sourceVersion.id] = newVersion.id;
            }

            // Ensure latestVersionId matches source (not just the last cloned version)
            if (sourceOnboarding.latestVersionId && versionIdMap[sourceOnboarding.latestVersionId]) {
                const correctLatestId = versionIdMap[sourceOnboarding.latestVersionId];
                const parentRef = doc(this.firestore, `projects/${projectId}/${this.collectionName}`, newOnboarding.id);
                await setDoc(parentRef, { latestVersionId: correctLatestId }, { merge: true });
            }

            return newOnboarding;
        } catch (error) {
            CoreFirebaseService.getInstance().logError(error, 'OnboardingService.cloneOnboarding');
            throw error;
        }
    }
}
